import { useEffect, useState } from "react";
import { validateImageFile, ErrorMessage } from "../../utlis/function";
import { callApi } from "../../services/ApiServices";
import { useGlobalStates } from "../../utlis/ClubsContext";
import ConfirmationModal from "../common/ConfirmationModal";
import toast from "react-hot-toast";
import { UserRole } from "../../utlis/constant";

const ScreensGallery = ({ maxImages = 5 }) => {
  const [images, setImages] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null); 

  const [errors, setErrors] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [imageIdToDelete, setImageIdToDelete] = useState(null);
  const { selectedClub, handleSelectedClubDetail, userRole } = useGlobalStates();

  useEffect(() => {
    if (selectedClub?.images?.length) {
      setImages(
        selectedClub?.images.map((img, index) => ({
          id: img.id,
          url: img.fileUrl,
          alt: `Screen placement ${index + 1}`,
        }))
      );
      setSelectedImageId(selectedClub?.websiteImage || null); 
    }
  }, [selectedClub]);

  const handleDeleteImage = async () => {
    try {
      setImageLoading(true);
      const response = await callApi({
        method: "delete",
        url: `club/delete/image/${selectedClub?.id}`,
        data: {
          fileId: imageIdToDelete,
        },
      });
      if (response.status === 200) {
        setImages(images.filter((img) => img.id !== imageIdToDelete));
        setErrors(errors.filter((error) => error.id !== imageIdToDelete));
        setOpenConfirmationModal(false);
        setImageIdToDelete(null);
        // Reset selected image if deleted
        if (selectedImageId === imageIdToDelete) {
          setSelectedImageId(null);
        }
        toast.success("Image deleted successfully", { id: "deleteImageToast" });
      }
    } catch (error) {
      console.log("delete Image Error", error);
    } finally {
      setImageLoading(false);
    }
  };

  const handleAddImages = () => {
    if (images.length >= maxImages) {
      return; // Don't allow adding more images if limit is reached
    }
    // Trigger file input
    document.getElementById("gallery-upload").click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setErrors([]);

    if (images.length + files.length > maxImages) {
      setErrors([
        { message: `You can only upload up to ${maxImages} images.` },
      ]);
      return;
    }

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const error = validateImageFile(file, 5);
      if (error) errors.push({ message: error });
      else validFiles.push(file);
    });

    if (errors.length) {
      setErrors(errors);
      return;
    }

    try {
      setImageLoading(true);

      const payload = new FormData();
      validFiles.forEach((file) => payload.append("file", file));

      const response = await callApi({
        method: "put",
        url: `club/upload/image/${selectedClub?.id}`,
        data: payload,
      });
      if (response.status === 200) {
        const uploadedImages = {
          id: response.id || Date.now(),
          url: response?.fileSaved?.fileUrl,
          alt: `Screen placement `,
        };
        setImages((prev) => [...prev, uploadedImages]);
        await handleSelectedClubDetail(selectedClub);
        toast.success("Image uploaded successfully!", { id: "uploadImageToast" });
      }
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setImageLoading(false);
      e.target.value = "";
    }
  };

  const handleSelectImage = async (imageId) => {
    if (userRole === UserRole.Viewer) return; 
    try {
      setImageLoading(true);
      const response = await callApi({
        method: "post",
        url: `club/${selectedClub?.id}/select-website-image`,
        data: { fileId: imageId },
      });
      if (response.status === 201) {
        setSelectedImageId(imageId);
        toast.success("Image selected for website display", { id: "selectImageToast" });
      }
    } catch (error) {
      console.error("Select image error", error);
      toast.error("Failed to select image");
    } finally {
      setImageLoading(false);
    }
  };

  // Check if we can add more images
  const canAddMore = images.length < maxImages;
  const remainingSlots = maxImages - images.length;
  const isDisabled = !canAddMore || userRole === UserRole.Viewer;

  return (
    <div className="bg-white rounded-xl p-6 main-shadow">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-black tracking-[0] leading-[normal]">
            Screens Placement Gallery
          </h2>
          {/* <p className="text-sm text-gray-500 mt-1">
            {images.length} of {maxImages} images uploaded
            {remainingSlots > 0 && ` • ${remainingSlots} slots remaining`}
          </p> */}
        </div>
        <button
          onClick={handleAddImages}
          disabled={isDisabled}
          className={`flex items-center gap-2 text-base font-medium transition-colors
    ${
      isDisabled
        ? "text-gray-400 cursor-not-allowed opacity-50"
        : "text-black hover:text-primary cursor-pointer"
    }
  `}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 3V13M3 8H13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {canAddMore ? "Add" : "Limit Reached"}
        </button>
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            Upload Errors:
          </h4>
          <div className="space-y-1">
            {errors.map((error, index) => (
              <ErrorMessage key={index} message={error.message} />
            ))}
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        id="gallery-upload"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple
        onChange={handleFileChange}
        className="hidden"
        disabled={!canAddMore}
      />

      {/* Gallery Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {imageLoading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10 rounded-lg">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        {images.map((image) => (
          <div key={image.id} className="relative group h-[200px]">
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-full object-cover rounded-lg"
            />

            {/* Selection Radio Button */}
            <div className="absolute bottom-2 left-2">
              <input
                type="radio"
                name="selectedImage"
                checked={selectedImageId === image?.id}
                onChange={() => handleSelectImage(image?.id)}
                disabled={userRole === UserRole.Viewer}
                className="w-4 h-4 text-primary bg-gray-100 border-gray-300 focus:ring-primary"
              />
              <label className="ml-1 text-sm text-white bg-black/50 px-1 rounded">
                Show on Website
              </label>
            </div>

            {/* Delete Button */}
            <button
              onClick={() => {
                setOpenConfirmationModal(true);
                setImageIdToDelete(image.id);
              }}
              className="cursor-pointer absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
              title="Delete image"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 3L3 9M3 3L9 9"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        ))}

        {/* Upload More Photos Card */}
        {canAddMore && !isDisabled && (
          <button
            onClick={handleAddImages}
            className="h-[200px] border-2 border-dashed bg-[#EFEFEF] border-gray-300 rounded-lg flex items-center justify-center hover:border-primary hover:bg-lightbg/30 transition-colors cursor-pointer group"
          >
            <div className="text-center">
              <svg
                className="w-8 h-8 mx-auto mb-2 text-gray-400 group-hover:text-primary transition-colors"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M17 8L12 3M12 3L7 8M12 3V15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-sm font-medium text-gray-600 group-hover:text-primary transition-colors">
                Upload More Photos
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {remainingSlots} slots remaining
              </p>
            </div>
          </button>
        )}
        {/* {loading && <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>} */}

        {/* Limit Reached Message */}
        {/* {!canAddMore && (
          <div className="h-[200px] border-2 border-dashed bg-gray-100 border-gray-300 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <svg
                className="w-8 h-8 mx-auto mb-2 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-sm font-medium text-gray-500">
                Maximum Images Reached
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {maxImages} of {maxImages} uploaded
              </p>
            </div>
          </div>
        )} */}
      </div>

      {/* Upload Guidelines */}
      {/* <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-medium text-blue-800 mb-1">Upload Guidelines:</h4>
        <ul className="text-xs text-blue-600 space-y-1">
          <li>• Maximum {maxImages} images allowed</li>
          <li>• Supported formats: JPG, PNG, WebP</li>
          <li>• Maximum file size: 5MB per image</li>
          <li>• Recommended: High-quality images for better display</li>
        </ul>
      </div> */}
      <ConfirmationModal
        isOpen={openConfirmationModal}
        title="Delete Image"
        description="Are you sure you want to delete image"
        onConfirm={handleDeleteImage}
        onCancel={() => setOpenConfirmationModal(false)}
        isLoading={imageLoading}
      />
    </div>
  );
};

export default ScreensGallery;